import React from 'react';
import { Link } from 'react-router-dom';
import { UserPlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * PortalAccountNotice — the full-screen explanation shown when a visitor is AUTHENTICATED
 * but has no usable portal customer behind that login.
 *
 * WHY THIS IS SHARED, AND WHY IT LIVES BEHIND ProtectedRoute
 * This markup used to exist only inside pages/Dashboard.js, gated on
 * `authResolved && customerNotFound`. Authentication alone was enough to get through
 * ProtectedRoute, so a contractor account signing in reached the portal, saw this message
 * on the dashboard, and then hit a permanently blank page on all 20 other routes — every
 * one of which loads via `if (customer?.customer_id) { …fetch… }`, a condition that is
 * simply false when `customer` is null. No fetch, no error, no explanation: the initial
 * empty state renders forever. Reported live 2026-08-24.
 *
 * TWO DISTINCT STATES — do not collapse them.
 *   notFound  — the lookup SUCCEEDED and returned nothing. Signing up is the real remedy.
 *   loadError — the lookup FAILED (RLS denial, network). The account may well exist, so
 *               telling this user to "sign up" would be actively wrong; offer a retry.
 * CustomerContext has kept these separate since 2d1acc2fa (audit A15), but `loadError` was
 * exposed and then read by NOTHING, so a failed lookup still rendered a blank shell — the
 * same defect A15 set out to remove, one layer further up.
 */
const Shell = ({ icon: Icon, iconTint, title, children, actions }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
    <div className="max-w-md w-full text-center">
      <div className="relative mb-6">
        <div className={`absolute inset-0 bg-gradient-to-br ${iconTint} rounded-full blur-2xl mx-auto w-24 h-24`}></div>
        <div className="relative w-24 h-24 mx-auto bg-gradient-to-br from-slate-700 to-slate-800 rounded-full flex items-center justify-center border border-blue-500/30">
          <Icon className="h-12 w-12 text-blue-400" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-white mb-3">{title}</h2>
      <p className="text-gray-300 mb-6">{children}</p>
      <div className="space-y-3">{actions}</div>
    </div>
  </div>
);

const primaryBtn = 'block w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25';
const secondaryBtn = 'block w-full px-6 py-3 bg-gray-700/50 text-gray-300 font-medium rounded-xl hover:bg-gray-600/50 transition-all border border-gray-600/30';

export const PortalAccountNotice = ({ variant, message }) => {
  if (variant === 'loadError') {
    return (
      <Shell
        icon={ExclamationTriangleIcon}
        iconTint="from-amber-500/20 to-red-500/20"
        title="We couldn't load your account"
        actions={
          <>
            <button type="button" onClick={() => window.location.reload()} className={primaryBtn}>
              Try Again
            </button>
            <Link to="/login" className={secondaryBtn}>Back to Login</Link>
          </>
        }
      >
        {message || 'Something went wrong reading your account details. This is usually temporary.'}
      </Shell>
    );
  }

  return (
    <Shell
      icon={UserPlusIcon}
      iconTint="from-blue-500/20 to-purple-500/20"
      title="No Customer Account Found"
      actions={
        <>
          <Link to="/signup" className={primaryBtn}>Sign Up for Customer Portal</Link>
          <Link to="/login" className={secondaryBtn}>Back to Login</Link>
        </>
      }
    >
      We couldn't find a customer account associated with your email. You may need to sign up
      or contact support for assistance.
    </Shell>
  );
};

export default PortalAccountNotice;
